import { Thumbnail } from '@vimily/bonjoro-ui-components-v2/VideoRecorder';
import { isAxiosError } from 'axios';
import { useGlobalEvent } from 'beautiful-react-hooks';
import { CallbackSetter } from 'beautiful-react-hooks/shared/types';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  saveGreet,
  useUploadFailedMutation,
  useUploadStartedMutation,
} from '~/App/Api/Greets';
import { useMetadata } from '~/App/Api/MessageTemplates';
import {
  uploadGreetFailed,
  useUploadImageToS3,
  useUploadVideoToS3,
} from '~/App/Api/S3Upload';
import { createThumbnailBlob } from '~/App/Api/Thumbnail';
import {
  RecorderDrawer,
  RecorderDrawerProps,
  RecordingFinishedPayload,
} from '~/App/Components/RecorderDrawer';
import CustomerContext from '~/App/Components/RecorderDrawer/Components/CustomerContext';
import { ProfileDetailsPanel } from '~/App/Components/RecorderDrawer/Components/Sidebar/ProfileDetailsPanel';
import {
  UploadErrorModal,
  UploadErrorType,
} from '~/App/Components/UploadErrorModal';
import { trackGreetEvent } from '~/App/Services/greet';
import {
  GREET_CONFIRM_SEND,
  GREET_RECORD_DISCARD,
  GREET_RECORD_REPLAY,
  GREET_RECORD_SETTINGS,
  GREET_RECORD_SETTINGS_CLOSE,
  GREET_RECORD_START,
  GREET_RECORD_STOP,
  GREET_SCHEDULER_CHANGE_SCHEDULE,
  GREET_SCHEDULER_LATER,
  GREET_SCHEDULER_LATER_CANCEL,
  GREET_SCHEDULER_LATER_CONFIRM,
  GREET_SCHEDULER_SCHEDULE_DELETE,
  GREET_SCHEDULER_TIME,
  GREET_SCHEDULER_TIMEZONE,
  GREET_TEMPLATE_SELECT,
  GREET_THUMBNAIL_SELECT,
  GREET_UPLOAD_ERROR,
  GREET_UPLOAD_SUCCESS,
  GREET_UPLOAD_VIDEO,
} from '~/generated/AnalyticsTypes';
import useAnalytics from '~/react-hooks/use-analytics';
import { useMessageTemplate } from '~/react-hooks/use-message-templates';
import { useOptimisedSending } from '~/react-hooks/use-optimised-sending';
import { usePreviousTemplateRecords } from '~/react-hooks/use-previous-template-record';
import {
  Greet,
  GreetSendType,
  GreetVideoSource,
  MessageTemplate,
  Timezone,
} from '~/types/models';

export enum StatusCodes {
  ConcurrentUpload = 4001,
}

const greetRecorderDrawerEventName = 'greet-recorder-drawer::open';

export const openGreetRecorderDrawer = (greet: Greet) =>
  window.dispatchEvent(
    new CustomEvent(greetRecorderDrawerEventName, { detail: { greet } })
  );

export interface GreetRecorderDrawerProps {}

export const GreetRecorderDrawer = () => {
  const analytics = useAnalytics();
  const { value: optimisedSending } = useOptimisedSending(false);
  const { previousTemplateRecords, setPreviousTemplateRecords } =
    usePreviousTemplateRecords();

  const [greet, setGreet] = useState<Greet>();
  const [open, setOpen] = useState(false);
  const [sendAt, setSendAt] = useState<DateTime>();
  const [sendType, setSendType] = useState<GreetSendType | undefined>(
    optimisedSending ? GreetSendType.Optimised : GreetSendType.Manual
  );
  const [messageTemplate, setMessageTemplate] = useState<MessageTemplate>();
  const [message, setMessage] = useState<string | undefined>('');
  const [isExternalVideo, setIsExternalVideo] = useState<boolean | undefined>(
    false
  );
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [videoBlob, setVideoBlob] = useState<Blob>();
  const [videoUrl, setVideoUrl] = useState<string | undefined>('');
  const [thumbnail, setThumbnail] = useState<Thumbnail>();
  const [uploadErrorType, setUploadErrorType] = useState<UploadErrorType>();

  const onGreetRecorderDrawer = useGlobalEvent(
    greetRecorderDrawerEventName as keyof WindowEventMap
  ) as unknown as CallbackSetter<Record<'detail', Record<'greet', Greet>>>;

  onGreetRecorderDrawer((event) => {
    setOpen(true);

    setGreet(event.detail.greet);
    setMessageTemplate(event.detail.greet?.message_template);
    setMessage(
      event.detail.greet?.message_template?.email_template?.message_template
    );
  });

  const onDrawerClose = () => {
    setVideoUrl('');
    setMessage('');
    setGreet(undefined);
    setMessageTemplate(undefined);
    setVideoBlob(undefined);
    setSendAt(undefined);
    setUploadProgress(0);
    setMessage('');
    setVideoBlob(undefined);
    setIsExternalVideo(false);
    setOpen(false);
  };

  const getPayload = useCallback(() => {
    if (!greet) return {};

    /**
     * Setting the sendType to Manual as default if Greet is a rollup or sendAt
     * has a value. If not, use the current sendType state.
     */
    const greetSendType =
      greet.profiles.length > 1 || sendAt ? GreetSendType.Manual : sendType;

    const payload = {
      source: 'web',
      send_type: greetSendType,
      video_source: isExternalVideo
        ? GreetVideoSource.ManualUpload
        : GreetVideoSource.Recorded,
    };

    if (sendAt) {
      payload['send_at'] = sendAt.toUTC().toString();
    }

    if (messageTemplate?.id) {
      payload['message_template_id'] = messageTemplate.id;
    }

    if (message != undefined && message.length > 0) {
      payload['message'] = message;
    }

    return payload;
  }, [message, messageTemplate, sendAt, sendType, isExternalVideo, greet]);

  const uploadStarted = useUploadStartedMutation();
  const uploadFailed = useUploadFailedMutation();
  const uploadImageToS3 = useUploadImageToS3();

  const onUploadError = (
    errorType: UploadErrorType = UploadErrorType.FailedUpload,
    errorMessage?: string
  ) => {
    if (!greet) return;

    uploadFailed.mutate(greet.id);

    analytics.track(GREET_UPLOAD_ERROR, {
      greet_id: greet.id,
      size: videoBlob!.size,
      message: errorMessage ?? 'Error uploading to server',
      templateType: messageTemplate?.template_type,
      greetType: greet.greet_type,
      sourceCreatedBy: greet.metadata?.source_created_by,
    });

    setUploadErrorType(errorType);
  };

  const uploadVideoToS3 = useUploadVideoToS3({
    onUploadProgress: setUploadProgress,
    currentProgress: 5,
    onUploadError: () => {
      if (!greet) return;

      onUploadError();
      uploadGreetFailed(greet.id);
    },
    onUploadSuccess: () => {
      if (messageTemplate) {
        if (greet?.campaign?.id) {
          previousTemplateRecords[greet.campaign.id] = messageTemplate.id;
          setPreviousTemplateRecords(previousTemplateRecords);
        }
      }
    },
  });

  /**
   * Current steps of Sending Greet:
   * 1. Set the status to uploading (uploadStarted)
   * 2. Update the Greet details with the constructed payload
   * 3. Create and upload the thumbnail to S3
   * 4. Upload the video to S3
   * 5. Redirects  'greet/{id}/redirect' to complete and update the Greet status
   */
  const onSubmit = useCallback(async () => {
    if (!videoBlob || !greet?.links) return;

    setUploadProgress(1);

    await uploadStarted.mutateAsync(greet.id);

    trackGreetEvent(GREET_CONFIRM_SEND, greet, {
      isOptimisedSending: optimisedSending,
      isManualScheduled: sendAt != undefined,
    });

    try {
      const startTime = new Date();

      setUploadProgress(5);

      await saveGreet(greet.id, getPayload());

      setUploadProgress(50);

      const thumbnailBlob = await createThumbnailBlob(thumbnail);
      if (thumbnailBlob) {
        await uploadImageToS3.mutateAsync({
          url: greet.links.putThumbnail,
          fileType: thumbnailBlob.type,
          file: thumbnailBlob,
        });
      }

      setUploadProgress(68);

      await uploadVideoToS3(greet.links.putVideo, videoBlob);

      const time = new Date().getTime() - startTime.getTime();

      setUploadProgress(100);

      analytics.track(GREET_UPLOAD_SUCCESS, {
        greetId: greet.id,
        time,
        source: 'web',
        templateType: messageTemplate?.template_type,
        greetType: greet.greet_type,
        sourceCreatedBy: greet.metadata?.source_created_by,
      });

      location.replace(`/greets/${greet.id}/redirect`);
    } catch (e) {
      let error = {
        errorType: UploadErrorType.FailedUpload,
        message: 'Unknown error.',
      };

      if (
        isAxiosError<{ data: { statusCode: StatusCodes } }>(e) &&
        e.response?.status === 400 &&
        e.response?.data?.data?.statusCode == StatusCodes.ConcurrentUpload
      ) {
        error = {
          errorType: UploadErrorType.ConcurrentUpload,
          message: e.message,
        };
      } else {
        error = {
          errorType: UploadErrorType.FailedUpload,
          message: isAxiosError(e) ? e.message : error.message,
        };
      }
      onUploadError(error.errorType, error.message);
    }
  }, [
    greet,
    greet?.links,
    thumbnail,
    videoUrl,
    videoBlob,
    getPayload,
    optimisedSending,
    sendAt,
  ]);

  const { data: metaData } = useMetadata({
    greetId: greet?.id ?? '',
    options: {
      enabled: greet != undefined,
    },
  });

  /**
   * If greet has a message template, use the message template from the greet.
   * If not, use the previously selected template
   */
  const selectedTemplateId = useMemo(() => {
    return (
      greet?.message_template?.id ??
      previousTemplateRecords[greet?.campaign?.id ?? '']
    );
  }, [greet, previousTemplateRecords]);

  const { data: fetchedMessageTemplate } = useMessageTemplate({
    templateUuid: selectedTemplateId,
    options: {
      enabled: selectedTemplateId !== undefined,
    },
  });

  useEffect(() => {
    if (!selectedTemplateId || Array.isArray(fetchedMessageTemplate)) return;

    setMessageTemplate(fetchedMessageTemplate?.data);
  }, [fetchedMessageTemplate, selectedTemplateId]);

  const onScheduleConfirm = (
    dateTime: DateTime,
    timezone?: Timezone,
    time?: string
  ) => {
    analytics.track(GREET_SCHEDULER_LATER_CONFIRM, {
      dateTime,
      timezone: timezone?.value,
      time,
    });
  };

  const onScheduleCancel = () => {
    analytics.track(GREET_SCHEDULER_LATER_CANCEL);
  };

  const onScheduleTimezoneChange = () => {
    analytics.track(GREET_SCHEDULER_TIMEZONE);
  };

  const onScheduleHourChange = () => {
    analytics.track(GREET_SCHEDULER_TIME);
  };

  const onRecorderPlay = () => {
    if (!greet) return;
    trackGreetEvent(GREET_RECORD_REPLAY, greet);
  };

  const onRecordingRedo = () => {
    if (!greet) return;
    trackGreetEvent(GREET_RECORD_DISCARD, greet);
  };

  const onRecordingStart = () => {
    if (!greet) return;
    trackGreetEvent(GREET_RECORD_START, greet);
  };

  const onRecordingStop = ({ metadata }: RecordingFinishedPayload) => {
    if (!greet) return;
    trackGreetEvent(GREET_RECORD_STOP, greet, {
      videoDuration: metadata?.duration,
    });
  };

  const onRecorderSettingsClick = () => {
    if (!greet) return;
    trackGreetEvent(GREET_RECORD_SETTINGS, greet, {});
  };

  const onRecorderSettingsCloseClick = (
    videoDevice?: MediaDeviceInfo,
    audioDevice?: MediaDeviceInfo
  ) => {
    if (!greet) return;

    trackGreetEvent(GREET_RECORD_SETTINGS_CLOSE, greet, {
      videoDevice: videoDevice?.label,
      audioDevice: audioDevice?.label,
    });
  };

  const onFileUpload = () => {
    if (!greet) return;
    trackGreetEvent(GREET_UPLOAD_VIDEO, greet);
  };

  const onMessageTemplateSelect = (messageTemplate?: MessageTemplate) => {
    if (!messageTemplate) return;
    setMessageTemplate(messageTemplate);
  };

  const onMessageTemplateSelectionClick = (
    messageTemplate?: MessageTemplate
  ) => {
    if (!greet || !messageTemplate) return;

    trackGreetEvent(
      GREET_TEMPLATE_SELECT,
      {
        ...greet,
        message_template: messageTemplate,
      },
      {
        email_layout: messageTemplate?.email_template?.layout,
      }
    );
  };

  const onThumbnailSelect = (thumbnail?: Thumbnail) => {
    setThumbnail(thumbnail);
  };

  const onThumbnailSelectionClick = () => {
    if (!greet) return;
    trackGreetEvent(GREET_THUMBNAIL_SELECT, greet);
  };

  const onScheduleRemove = () =>
    analytics.track(GREET_SCHEDULER_SCHEDULE_DELETE);
  const onScheduleLater = () => analytics.track(GREET_SCHEDULER_LATER);
  const onChangeScheduleClick = () =>
    analytics.track(GREET_SCHEDULER_CHANGE_SCHEDULE);

  const topSidebarComponent = () => {
    if (!greet) return <></>;

    return (
      <>
        <ProfileDetailsPanel />
        {!videoBlob && (
          <CustomerContext greet={greet} className='border-0 p-0' />
        )}
      </>
    );
  };

  const recorderDrawerProps: RecorderDrawerProps = {
    open,
    messageTemplate,
    message: message ?? metaData?.defaultMessage,
    profiles: greet?.profiles,
    defaultSubjectLine: metaData?.defaultSubjectLine,
    defaultMessage: metaData?.defaultMessage,
    uploadProgress,

    onSubmit,
    onDrawerClose,

    onRecorderPlay,
    onRecordingStart,
    onRecordingRedo,
    onRecordingStop,
    onRecorderSettingsClick,
    onRecorderSettingsCloseClick,

    onScheduleConfirm,
    onScheduleRemove,
    onScheduleLater,
    onChangeScheduleClick,
    onScheduleCancel,
    onScheduleTimezoneChange,
    onScheduleHourChange,

    onMessageTemplateSelect,
    onMessageTemplateSelectionClick,
    onMessageChange: setMessage,
    onSendTypeChange: setSendType,
    onSchedulerSendAtChange: setSendAt,
    onVideoBlobChange: setVideoBlob,
    onVideoUrlChange: setVideoUrl,
    onRecorderToggleExternalVideo: setIsExternalVideo,
    onThumbnailSelect,
    onThumbnailSelectionClick,
    onFileUpload,

    topSidebarComponent: topSidebarComponent(),
    demoTemplateAsDefault: true,
  };

  return (
    <>
      <RecorderDrawer {...recorderDrawerProps} />
      <UploadErrorModal
        errorType={uploadErrorType}
        open={uploadErrorType != undefined}
        onModalClose={() => {
          setUploadErrorType(undefined);
          setUploadProgress(0);
        }}
        downloadLink={videoUrl}
      />
    </>
  );
};
